/**
 * AdminPanel — ATOMS Design v2 shell
 * Roles: oem_admin | distributor_admin
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AtomsLogo from '../assets/AtomsLogo.png';
import UsersTab    from './admin/UsersTab';
import MachinesTab from './admin/MachinesTab';
import OrgsTab     from './admin/OrgsTab';
import { getMe }   from '../API/adminAPI';

const Icon = ({ d, size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const BackIcon  = 'M19 12H5 M12 19l-7-7 7-7';
const UsersIcon = 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 11a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75';
const CpuIcon   = 'M12 2a2 2 0 012 2v1h4a1 1 0 011 1v3h1a1 1 0 011 1v6a1 1 0 01-1 1h-1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3H3a1 1 0 01-1-1V9a1 1 0 011-1h1V5a1 1 0 011-1h4V4a2 2 0 012-2z';
const OrgIcon   = 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10';

const NAV_ICONS = { users: UsersIcon, machines: CpuIcon, distributors: OrgIcon };

// ── Nav item ─────────────────────────────────────────────────────────────────
function NavItem({ value, label, active, onClick }) {
  return (
    <button
      onClick={() => onClick(value)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        width: '100%', height: 36, padding: '0 10px',
        borderRadius: 'var(--r-md)', border: 'none',
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--ink-500)',
        fontFamily: 'var(--font-sans)', fontSize: 13,
        fontWeight: active ? 500 : 400,
        cursor: 'pointer', transition: 'all var(--t-fast)',
        marginBottom: 2, textAlign: 'left',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-raised)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon d={NAV_ICONS[value] || UsersIcon} size={15} color={active ? 'var(--accent)' : 'var(--ink-500)'} />
      {label}
    </button>
  );
}

// ── AdminPanel ────────────────────────────────────────────────────────────────
export default function AdminPanel() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const role      = localStorage.getItem('role') || 'user';
  const orgName   = localStorage.getItem('org_name') || '';

  const [meError,    setMeError]    = useState('');
  const [activeTab,  setActiveTab]  = useState(location.state?.tab || 'users');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (role !== 'oem_admin' && role !== 'distributor_admin') {
      navigate('/app/dashboard', { replace: true }); return;
    }
    getMe().catch(e => setMeError(e?.response?.data?.error || 'Could not verify admin access'));
    if (location.state?.refresh) {
      if (location.state?.tab) setActiveTab(location.state.tab);
      setRefreshKey(k => k + 1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line

  if (role !== 'oem_admin' && role !== 'distributor_admin') return null;

  const roleLabel = role === 'oem_admin' ? 'OEM Admin' : 'Distributor Admin';
  const navItems  = [
    { value: 'users',    label: 'Users'        },
    { value: 'machines', label: 'Machines'     },
    ...(role === 'oem_admin' ? [{ value: 'distributors', label: 'Distributors' }] : []),
  ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', overflow: 'hidden', fontFamily: 'var(--font-sans)' }}>

      {/* ── Header ── */}
      <header style={{
        height: 56, flexShrink: 0,
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border-hair)',
        display: 'flex', alignItems: 'center',
        padding: '0 20px', gap: 14,
      }}>
        <button
          onClick={() => navigate('/app/dashboard')}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--r-md)', padding: '5px 10px',
            color: 'var(--ink-500)', fontSize: 12,
            fontFamily: 'var(--font-sans)', cursor: 'pointer',
            transition: 'all var(--t-fast)',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <Icon d={BackIcon} size={13} />
          Back
        </button>

        <img src={AtomsLogo} alt="ATOMS" style={{ height: 24, width: 24, objectFit: 'contain' }} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-900)', lineHeight: 1.2 }}>
            Admin Panel
          </div>
          {orgName && (
            <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 1 }}>{orgName}</div>
          )}
        </div>

        {/* Role badge */}
        <span className={`role-badge ${role === 'oem_admin' ? 'oem' : 'dist'}`}>
          {roleLabel}
        </span>
      </header>

      {/* ── Body ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Sidebar */}
        <aside style={{
          width: 220, flexShrink: 0,
          background: 'var(--bg-surface)',
          borderRight: '1px solid var(--border-hair)',
          padding: '20px 8px',
          display: 'flex', flexDirection: 'column',
          overflowY: 'auto',
        }}>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--ink-300)', padding: '0 10px', marginBottom: 8 }}>
            Manage
          </div>

          {navItems.map(item => (
            <NavItem key={item.value} active={activeTab === item.value} onClick={setActiveTab} {...item} />
          ))}

          <div style={{ flex: 1 }} />

          <button
            onClick={() => navigate('/app/dashboard')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 10px', borderRadius: 'var(--r-md)',
              fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-300)',
              transition: 'all var(--t-fast)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-raised)'; e.currentTarget.style.color = 'var(--ink-500)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--ink-300)'; }}
          >
            <Icon d={BackIcon} size={13} />
            Back to App
          </button>
        </aside>

        {/* Content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '28px 36px' }}>
          {meError && (
            <div style={{ background: 'var(--offline-bg)', color: 'var(--offline)', border: '1px solid var(--offline-border)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 20 }}>
              {meError}
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              {activeTab === 'users'        && <UsersTab    key={`u-${refreshKey}`} />}
              {activeTab === 'machines'     && <MachinesTab key={`m-${refreshKey}`} />}
              {activeTab === 'distributors' && role === 'oem_admin' && <OrgsTab key={`o-${refreshKey}`} />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
